import { useQuery } from '@tanstack/react-query';
import { api, type Competition } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// useCompChecklist — the comp go-live checklist, TRANSPLANTED from
// components/wizard/CompetitionWizard.tsx (now retired — see TournamentSetup
// header comment). Same derivation, same react-query keys (['orgs'],
// ['competitions'], ['competitionClans', compId], ['compStages', compId]) —
// this shares the SAME cache the cockpit already reads, so it can never drift
// from what the tabs themselves show, and it resumes correctly no matter
// where an admin left off.
//
// 7-item go-live checklist (Lisa's Phase 4a brief, +2 items 2026-07-18 —
// rules-defaults + public-pages surfacing goal):
//   1. Org linked      — org bound to Discord
//   2. Competition created — name/dates/team size (bundled in one create POST)
//   3. Stages composed — the stage pipeline this comp will run
//   4. Clans enrolled  — at least one clan fielding a team
//   5. Activated       — comp status is LIVE
//   6. Rules           — informational/non-blocking: every comp is SEEDED with
//                         default rules text server-side (a comp can never go
//                         live "broken"), so this tracks whether an admin has
//                         actually customised them (rulesVersion > 1), not
//                         whether rules exist at all. Deliberately carries no
//                         `tab` — it must NOT gate the Competition tool-strip
//                         tab's own "done" dot (doneMap.competition in
//                         TournamentSetup), since staying on the default is a
//                         legitimate, common choice, not an error.
//   7. Public page     — comp's authored public page (CompPublicPageEditor)
//                         is published. Mounted INLINE inside TournamentSetup's
//                         Competition tab (CompPane) now — moved off the old
//                         /me/comp/:compId route 2026-07-18 (setup-consolidation
//                         goal: authoring a public page is an operator action,
//                         so it lives in Setup with the rest of comp setup;
//                         "My Comp" keeps a view-only pointer to it).
//
// Each item carries a `tab` (which StepId of TournamentSetup hosts it) and a
// `sectionId` (the on-page anchor useSectionHighlight scrolls/pulses/focuses).
// `activated` has no `tab` — the Activate control lives in the anchor row,
// visible above every tab, not inside a specific one. `rules` and `publicPage`
// both have no `tab` for the same reason: their controls only exist on the
// competition tab, but neither should gate that tab's own "done" dot
// (doneMap.competition in TournamentSetup filters on `tab === 'competition'`)
// — staying on defaults / not publishing yet are legitimate, non-blocking
// states, not errors. TournamentSetup's performJump force-switches to the
// competition tab for both by id instead of via `tab`.
// ─────────────────────────────────────────────────────────────────────────────

export type CompChecklistItem = {
  id: 'orgLinked' | 'created' | 'stages' | 'clans' | 'activated' | 'rules' | 'publicPage';
  label: string;
  done: boolean;
  detail: string;
  tab?: 'org' | 'competition' | 'clans';
  sectionId?: string;
  /** No comp item sets this any more (publicPage moved on-page 2026-07-18) —
   * kept optional purely so GuideMeLauncher's `CompChecklistItem |
   * OrgChecklistItem` union (which still needs it for the org checklist's
   * `branding` item) type-checks without a cast. */
  externalHref?: string;
};

export type CompChecklist = {
  items: CompChecklistItem[];
  readyCount: number;
  totalCount: number;
  activeOrgId: string;
  activeCompId: string;
  activeComp?: Competition;
};

export function useCompChecklist(orgId?: string, compId?: string): CompChecklist {
  const { data: orgs } = useQuery({ queryKey: ['orgs'], queryFn: api.orgs });
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  const orgList = orgs ?? [];
  const compList = comps ?? [];
  const activeOrgId = orgId || orgList[0]?.id || '';
  const activeOrg = orgList.find(o => o.id === activeOrgId);

  // Same normalized-comparison reasoning TournamentSetup's CompPane already
  // gives: comp.orgId is stored bare, some legacy rows carry the ORG#-prefixed
  // value — compare both sides stripped.
  const bareOrgId = activeOrgId.replace(/^ORG#/, '');
  const orgComps = bareOrgId
    ? compList.filter(c => (c.orgId ?? '').replace(/^ORG#/, '') === bareOrgId)
    : [];
  const pendingComp = orgComps.find(c => c.status === 'PENDING');
  const activeComp: Competition | undefined = compId
    ? orgComps.find(c => c.id === compId)
    : (pendingComp ?? orgComps[0]);
  const compExists = !!activeComp;
  const activeCompId = activeComp?.id ?? '';

  const { data: clans } = useQuery({
    queryKey: ['competitionClans', activeCompId],
    queryFn: () => api.competitionClans(activeCompId),
    enabled: !!activeCompId,
  });
  const { data: stages } = useQuery({
    queryKey: ['compStages', activeCompId],
    queryFn: () => api.listCompStages(activeCompId),
    enabled: !!activeCompId,
  });
  // Rules — SAME query key CompetitionCreateForm's edit-mode rulesQuery
  // already uses (['compTermsAcceptance', compId]), so this can never drift
  // from what that textarea shows/prefills. Every comp is seeded with
  // rulesVersion 1 server-side (src/shared/compTerms.ts) — an admin PATCH
  // that changes `rules` bumps it — so "customised" is simply > 1.
  const { data: termsAcceptance } = useQuery({
    queryKey: ['compTermsAcceptance', activeCompId],
    queryFn: () => api.getCompTermsAcceptance(activeCompId),
    enabled: !!activeCompId,
  });
  // Public page — SAME query key CompPublicPageEditor.tsx uses
  // (['compPublicPage', compId] → api.getCompPublicPage(compId), the authed
  // draft CRUD endpoint). Deliberately NOT Competition.publicPageEnabled —
  // that field is a different, unrelated toggle (the opt-in /watch/:compId
  // spectator page flipped via PublicPageToggle — see CommandCenter.tsx's own
  // header comment on that field); gating this item on it would incorrectly
  // read "not published" for a comp that has fully authored+published its
  // real public page but never opted into the separate spectator page.
  const { data: compPublicPage } = useQuery({
    queryKey: ['compPublicPage', activeCompId],
    queryFn: () => api.getCompPublicPage(activeCompId),
    enabled: !!activeCompId,
  });

  const clanCount = clans?.length ?? 0;
  const stageCount = stages?.length ?? 0;
  const rulesVersion = termsAcceptance?.rulesVersion ?? 1;

  const orgLinked = !!activeOrg?.primaryGuildId;
  const stagesDone = compExists && stageCount > 0;
  const clansDone = compExists && clanCount > 0;
  const activated = activeComp?.status === 'LIVE';
  const rulesCustomised = compExists && rulesVersion > 1;
  const publicPagePublished = compExists && !!compPublicPage?.published;

  const items: CompChecklistItem[] = [
    {
      id: 'orgLinked',
      label: 'Org linked',
      done: orgLinked,
      detail: orgLinked ? 'linked' : 'not linked',
      tab: 'org',
      sectionId: 'org-discord',
    },
    {
      id: 'created',
      label: 'Competition created',
      done: compExists,
      detail: compExists ? (activeComp!.name || 'created') : 'not created',
      tab: 'competition',
      sectionId: 'comp-create',
    },
    {
      id: 'stages',
      label: 'Stages composed',
      done: stagesDone,
      detail: `${stageCount} composed`,
      tab: 'competition',
      sectionId: 'comp-stages',
    },
    {
      id: 'clans',
      label: 'Clans enrolled',
      done: clansDone,
      detail: `${clanCount} enrolled`,
      tab: 'clans',
      sectionId: 'clans-workbench',
    },
    {
      id: 'activated',
      label: 'Activated',
      done: activated,
      detail: activeComp?.status ?? '—',
      sectionId: 'status-activate',
    },
    {
      id: 'rules',
      label: 'Rules',
      done: rulesCustomised,
      // Informational, not a pass/fail — v1 is the healthy seeded default,
      // not a broken/incomplete state. No `tab` (see header comment): this
      // must not gate the Competition tool-strip tab's "done" dot.
      detail: !compExists ? 'no competition yet' : (rulesCustomised ? 'customised' : 'default — review'),
      sectionId: 'comp-rules',
    },
    {
      id: 'publicPage',
      label: 'Public page',
      done: publicPagePublished,
      detail: !compExists ? 'no competition yet' : (publicPagePublished ? 'published' : 'not published'),
      // No `tab` (see header comment) — force-switched to 'competition' by
      // TournamentSetup's performJump instead. sectionId anchors the editor
      // mounted inline in CompPane, next to CompetitionCreateForm.
      sectionId: 'comp-publicpage',
    },
  ];

  return {
    items,
    readyCount: items.filter(i => i.done).length,
    totalCount: items.length,
    activeOrgId,
    activeCompId,
    activeComp,
  };
}
